import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { clearAllCache } from "../store/cacheSlice";
import { setCustomAuthToken } from "./tokenStore";
import { CONFIG } from "../constants";

const SessionAuthContext = createContext();

export const useSessionAuth = () => useContext(SessionAuthContext);

export const SessionAuthProvider = ({ children }) => {
  const [customToken, setCustomToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authFailed, setAuthFailed] = useState(false);
  const [roles, setRoles] = useState(null);
  const dispatch = useDispatch();

  const checkSession = async () => {
    try {
      console.log("🔍 Checking session with:", CONFIG.API_BASE_URL);

      dispatch(clearAllCache());

      const res = await axios.get(
        CONFIG.API_BASE_URL + "/Authentication/session",
        {
          withCredentials: true,
          timeout: 10000, // 10 second timeout
        }
      );

      console.log("Session response:", res.data);

      if (!res.data.success) {
        throw new Error("No valid session");
      }

      const token = res.data.data;
      const decoded = jwtDecode(token);
      console.log("Decoded token:", decoded);

      if (!decoded?.IsAdmin) {
        throw new Error("Not an admin");
      }

      setCustomToken(token);
      setCustomAuthToken(token);
      setRoles({
        IsAdmin: decoded.IsAdmin,
        IsSuperAdmin: decoded.IsSuperAdmin,
      });
      setAuthFailed(false);
    } catch (err) {
      console.error("Session check failed:", err);
      setAuthFailed(true);

      // Redirect to login
      // Redirect with loop prevention using sessionStorage
      setTimeout(() => {
        const retryCount = parseInt(
          sessionStorage.getItem("authRedirectCount") || "0",
          10
        );

        if (retryCount < 1) {
          sessionStorage.setItem("authRedirectCount", retryCount + 1);
          window.location.href = CONFIG.API_BASE_URL + "/auth/login";
        } else {
          console.warn(
            "🛑 Preventing redirect loop: Max auth retries reached."
          );
        }
      }, 1000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const logout = () => {
    dispatch(clearAllCache());
    setCustomToken(null);
    setRoles(null);
    setCustomAuthToken(null);
    localStorage.clear();

    // Redirect to logout
    window.location.href = CONFIG.API_BASE_URL + "/auth/logout";
  };

  return (
    <SessionAuthContext.Provider
      value={{
        customToken,
        roles,
        isAuthenticated: !!customToken && !authFailed,
        logout,
        loading,
        recheckSession: checkSession, // Allow manual recheck
      }}
    >
      {!loading && !authFailed && roles && children}

      {/* Show loading state */}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <div>
            <h3>🔄 Loading...</h3>
            <p>Checking authentication...</p>
            {CONFIG.IS_DEVELOPMENT && <small>API: {CONFIG.API_BASE_URL}</small>}
          </div>
        </div>
      )}

      {/* Show auth failed state */}
      {!loading && authFailed && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <h2>🔐 Authentication Required</h2>
          <p>Redirecting to login...</p>
          <button
            onClick={() =>
              (window.location.href = CONFIG.API_BASE_URL + "/auth/login")
            }
            style={{
              padding: "10px 20px",
              marginTop: "10px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Login Now
          </button>
          {CONFIG.IS_DEVELOPMENT && (
            <div style={{ marginTop: "20px", fontSize: "12px", color: "#666" }}>
              <p>API URL: {CONFIG.API_BASE_URL}</p>
              <p>Frontend URL: {CONFIG.FRONTEND_BASE_URL}</p>
            </div>
          )}
        </div>
      )}
    </SessionAuthContext.Provider>
  );
};
